import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, CircularProgress, Stack, Typography } from '@mui/material';

import { getSearchCategoryLabelKey } from '../../model/categories';
import { SearchCategory, SearchHitEntry } from '../../model/types';
import SearchResultRow from '../SearchResultRow';

interface Props {
  category: SearchCategory;
  entries: SearchHitEntry[];
  query: string;
  activeIndex: number;
  isLoading: boolean;
  totalElements: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  onOpen: (entry: SearchHitEntry) => void;
  onActivate: (index: number) => void;
}

const SearchCategoryResults: FC<Props> = ({
  category,
  entries,
  query,
  activeIndex,
  isLoading,
  totalElements,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onOpen,
  onActivate,
}) => {
  const { t } = useTranslation();
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onLoadMore();
      },
      { root: node.closest('[data-search-scroll]'), rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, onLoadMore, entries.length]);

  if (isLoading) {
    return (
      <Stack alignItems="center" padding="2rem">
        <CircularProgress size="1.5rem" />
      </Stack>
    );
  }

  return (
    <Stack data-testid="search-drill-in">
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        padding="0.75rem 0.625rem 0.375rem"
      >
        <Typography sx={{ fontSize: '1rem', fontWeight: 400, lineHeight: 1.25, color: 'grey.900' }}>
          {t(getSearchCategoryLabelKey(category))}
        </Typography>
        <Typography
          sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.25, color: 'grey.700' }}
        >
          {t('search.group_count', { count: totalElements })}
        </Typography>
      </Stack>

      {entries.map((entry, index) => (
        <SearchResultRow
          key={entry.hit.id}
          hit={entry.hit}
          category={entry.category}
          query={query}
          isActive={index === activeIndex}
          onOpen={() => onOpen(entry)}
          onActivate={() => onActivate(index)}
        />
      ))}

      {hasNextPage && (
        <Stack
          ref={sentinelRef}
          alignItems="center"
          padding="0.75rem"
          data-testid="search-drill-in-sentinel"
        >
          {isFetchingNextPage && <CircularProgress size="1.25rem" />}
        </Stack>
      )}

      {!hasNextPage && entries.length > 0 && <Box paddingBottom="0.5rem" />}
    </Stack>
  );
};

export default SearchCategoryResults;
